import Phaser from 'phaser';
import { GibberishDialogueSystem } from '@/systems/GibberishDialogueSystem';
import type { SentienceLevel } from '@/types';

/**
 * World-space vocalization bubble that evolves with the blob's sentience.
 *
 * The blob experiences sound differently as it grows aware:
 *   L0: sound is sound — pulsing rings only, no text
 *   L1: flickering symbols — single-char noise marks
 *   L2: gibberish shapes — scrambled characters with rare real words
 *   L3: partial comprehension — more readable fragments mixed with gibberish
 *   L4: clear speech — full text
 *
 * One SpeechBubble can be reused across many lines. Call `say` each time.
 */
const BG_PAD_X = 10;
const BG_PAD_Y = 6;
const BASE_OFFSET_Y = -38;

const gibberish = new GibberishDialogueSystem();

export interface SpeechBubbleOptions {
  fontSize?: string;
  mobileFontSize?: string;
  color?: string;
}

export interface VocalizationOwner {
  x: number;
  y: number;
  active: boolean;
}

export class SpeechBubble {
  private bg: Phaser.GameObjects.Rectangle;
  private text: Phaser.GameObjects.Text;
  private ring: Phaser.GameObjects.Arc;
  private visible = false;
  private hideTimer: Phaser.Time.TimerEvent | null = null;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly owner: VocalizationOwner,
    options: SpeechBubbleOptions = {},
  ) {
    const mobile = SpeechBubble.detectMobile();
    const fontSize = mobile
      ? options.mobileFontSize ?? '20px'
      : options.fontSize ?? '15px';

    this.ring = scene.add
      .circle(owner.x, owner.y + BASE_OFFSET_Y, 8, 0xffffff, 0)
      .setStrokeStyle(1.5, 0xffffff, 0)
      .setDepth(500);

    this.bg = scene.add
      .rectangle(owner.x, owner.y + BASE_OFFSET_Y, 10, 10, 0x000000, 0.65)
      .setDepth(501)
      .setVisible(false);

    this.text = scene.add
      .text(owner.x, owner.y + BASE_OFFSET_Y, '', {
        fontSize,
        color: options.color ?? '#e8e8e8',
        fontFamily: 'monospace',
        stroke: '#000000',
        strokeThickness: 2,
      })
      .setOrigin(0.5)
      .setDepth(502)
      .setVisible(false);
  }

  say(line: string, sentience: SentienceLevel, durationMs = 2800): void {
    this.cancelHide();

    // Pulse ring for the "sound" part — always plays
    this.pulseRing();

    if (sentience >= 1) {
      const rendered =
        sentience >= 4 ? line : gibberish.renderString(line, { sentienceLevel: sentience });
      this.text.setText(rendered).setVisible(true);
      const w = this.text.width + BG_PAD_X * 2;
      const h = this.text.height + BG_PAD_Y * 2;
      this.bg.setSize(w, h).setVisible(true);
    } else {
      this.text.setVisible(false);
      this.bg.setVisible(false);
    }

    this.visible = true;
    this.hideTimer = this.scene.time.delayedCall(durationMs, () => this.hide());
  }

  /** Call each frame (or on owner move) to keep the bubble tracking. */
  follow(): void {
    if (!this.owner.active) {
      this.hide();
      return;
    }
    const x = this.owner.x;
    const y = this.owner.y + BASE_OFFSET_Y;
    this.ring.setPosition(x, y);
    this.bg.setPosition(x, y);
    this.text.setPosition(x, y);
  }

  destroy(): void {
    this.cancelHide();
    this.ring.destroy();
    this.bg.destroy();
    this.text.destroy();
  }

  private pulseRing(): void {
    this.ring
      .setScale(0.6)
      .setAlpha(0.8);
    this.ring.setStrokeStyle(1.5, 0xffffff, 0.8);
    this.scene.tweens.add({
      targets: this.ring,
      scale: 2.2,
      alpha: 0,
      duration: 800,
      ease: 'Cubic.easeOut',
    });
  }

  private hide(): void {
    this.visible = false;
    this.text.setVisible(false);
    this.bg.setVisible(false);
    this.ring.setAlpha(0);
  }

  private cancelHide(): void {
    this.hideTimer?.remove();
    this.hideTimer = null;
  }

  private static detectMobile(): boolean {
    if (typeof window === 'undefined') return false;
    return window.matchMedia?.('(max-width: 900px), (pointer: coarse)').matches ?? false;
  }
}
